import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { Reveal } from "./Reveal";

const POSTER = "/story/origin-film-poster.webp";
const VIDEO = "/videos/origin_film.mp4";
const AMBIENT = "/audio/pencil-ambient.wav";

export const CinematicFilm = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [canPlay, setCanPlay] = useState(false);
  const [failed, setFailed] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [inView, setInView] = useState(false);

  const reduced = typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saveData = typeof navigator !== "undefined" && navigator.connection?.saveData;
  const useVideo = !reduced && !saveData && !failed;

  useEffect(() => {
    if (!useVideo || !ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => {
        setInView(e.isIntersecting);
        if (e.isIntersecting) {
          setCanPlay(true);
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [useVideo]);

  // Ambient graphite sound follows the sound toggle + viewport visibility.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (soundOn && inView) {
      a.volume = 0.7;
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [soundOn, inView]);

  const toggleSound = () => setSoundOn((s) => !s);

  return (
    <section ref={ref} data-testid="cinematic-film" aria-label="Black Dog Society film — the first line" className="relative h-[80vh] md:h-screen w-full overflow-hidden bg-black">
      {useVideo ? (
        <video
          ref={videoRef}
          data-testid="origin-film-video"
          muted
          loop
          playsInline
          preload="none"
          poster={POSTER}
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          width="1280"
          height="720"
        >
          {canPlay && <source src={VIDEO} type="video/mp4" />}
        </video>
      ) : (
        <img
          src={POSTER}
          alt="A graphite pencil touching paper in a dark Black Dog Society studio"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          loading="lazy"
          width="1280"
          height="720"
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-bds-bg via-transparent to-bds-bg/60" />

      <audio ref={audioRef} src={AMBIENT} loop preload="none" data-testid="film-ambient-audio" />

      {/* Sound toggle — invites visitors to hear the pencil on paper */}
      <div className="absolute top-6 right-6 md:top-8 md:right-10 z-20 flex items-center gap-3">
        <AnimatePresence>
          {!soundOn && (
            <motion.span
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.5 }}
              className="hidden sm:inline font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-muted"
            >
              Hear the pencil
            </motion.span>
          )}
        </AnimatePresence>
        <button
          type="button"
          onClick={toggleSound}
          data-testid="film-sound-toggle"
          aria-pressed={soundOn}
          aria-label={soundOn ? "Mute film sound" : "Unmute film sound"}
          className="group relative h-11 w-11 flex items-center justify-center rounded-full border border-bds-border/70 bg-black/40 backdrop-blur-sm text-bds-fg/80 hover:text-bds-fg hover:border-bds-fg/60 transition-colors"
        >
          {soundOn ? (
            <Volume2 className="h-4 w-4" strokeWidth={1.5} />
          ) : (
            <VolumeX className="h-4 w-4" strokeWidth={1.5} />
          )}
          {soundOn && (
            <span className="absolute inset-0 rounded-full border border-bds-accent/50 animate-ping" />
          )}
        </button>
      </div>


      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
        <Reveal>
          <span className="font-mono-bds text-[10px] md:text-xs uppercase tracking-[0.35em] text-bds-muted">
            Film 001 — The First Line
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <motion.h2 className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-5 max-w-3xl">
            We draw what <span className="italic text-bds-muted">we can&apos;t say.</span>
          </motion.h2>
        </Reveal>
      </div>
    </section>
  );
};
